import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AppWindow, Search, Store as StoreIcon } from 'lucide-react';
import { api, type OverviewRow } from '@/lib/callables';
import { useMyStores } from '@/features/stores/StoresPage';
import { AppGlyph, StoreGlyph } from '@/components/StoreGlyph';
import { PlatformBadges } from '@/components/PlatformBadges';
import { VERSION_STATUS_META, type VersionStatusKey } from '@/components/VersionStatusBadge';
import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/lib/utils';

interface AppHit {
  storeId: string;
  storeName: string;
  storeColor: string | null;
  storeIcon: string | null;
  appId: string;
  appName: string;
  iconUrl: string | null;
  platforms: string[];
  devices: string[] | null;
  status: VersionStatusKey;
  /** The app has a published (READY_FOR_SALE) version on the App Store. */
  live: boolean;
  /** Lower sorts first: 0 exact name, 1 name starts with query, 2 name has every word, 3 matched via bundle id / store. */
  rank: number;
}

const STATUS_PRIORITY: Record<VersionStatusKey | 'none', number> = {
  rejected: 0,
  inReview: 1,
  waiting: 2,
  approved: 3,
  draft: 4,
  live: 5,
  none: 6,
};

/**
 * Top-bar search across every store and app the user can see.
 * Stores come from the live query; the app index piggybacks on appsOverview
 * (already cached for the dashboard cards) — no extra backend needed.
 */
export function GlobalSearch() {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrap = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const stores = useMyStores();

  const overview = useQuery({
    queryKey: ['apps-overview'],
    queryFn: () => api.appsOverview({}),
    staleTime: 5 * 60_000,
    enabled: open, // fetch lazily the first time the search is used
  });

  // ⌘K / Ctrl-K focuses the search from anywhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        input.current?.focus();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const needle = q.trim().toLowerCase();

  const storeHits = useMemo(() => {
    if (!needle) return [];
    return stores.rows
      .filter((s) => s.data.name.toLowerCase().includes(needle))
      .slice(0, 5);
  }, [stores.rows, needle]);

  // Word-based matching: every word of the query must appear somewhere in the
  // app name, bundle id, or store name — order-independent, so "rvira printer"
  // and "printer app" both find "Rvira Printer App". Best name matches first.
  const appMatches = useMemo<{ hits: AppHit[]; total: number }>(() => {
    if (!needle) return { hits: [], total: 0 };
    const tokens = needle.split(/\s+/).filter(Boolean);
    const seen = new Map<string, AppHit>();
    for (const row of (overview.data?.rows ?? []) as OverviewRow[]) {
      const name = row.appName.toLowerCase();
      const haystack = `${name} ${row.bundleId.toLowerCase()} ${row.storeName.toLowerCase()}`;
      if (!tokens.every((token) => haystack.includes(token))) continue;
      const rank = name === needle ? 0
        : name.startsWith(needle) ? 1
        : tokens.every((token) => name.includes(token)) ? 2
        : 3;
      const key = `${row.storeId}/${row.appId}`;
      const status = row.bucket === 'none' ? 'none' : row.bucket;
      const existing = seen.get(key);
      if (!existing) {
        seen.set(key, {
          storeId: row.storeId,
          storeName: row.storeName,
          storeColor: row.storeColor,
          storeIcon: row.storeIcon,
          appId: row.appId,
          appName: row.appName,
          iconUrl: row.iconUrl,
          platforms: row.platforms ?? [],
          devices: row.devices ?? null,
          status: status === 'none' ? 'draft' : status,
          live: row.hasLive,
          rank,
        });
      } else {
        if (STATUS_PRIORITY[status] < STATUS_PRIORITY[existing.status]) {
          existing.status = status === 'none' ? 'draft' : status;
        }
        existing.live ||= row.hasLive;
        existing.rank = Math.min(existing.rank, rank);
      }
    }
    const sorted = [...seen.values()].sort((a, b) => a.rank - b.rank || a.appName.localeCompare(b.appName));
    return { hits: sorted.slice(0, 30), total: sorted.length };
  }, [overview.data, needle]);
  const appHits = appMatches.hits;

  const flat = useMemo(
    () => [
      ...storeHits.map((s) => ({ type: 'store' as const, to: `/stores/${s.id}`, key: s.id })),
      ...appHits.map((a) => ({ type: 'app' as const, to: `/stores/${a.storeId}/apps/${a.appId}`, key: `${a.storeId}/${a.appId}` })),
    ],
    [storeHits, appHits],
  );

  useEffect(() => setActive(0), [needle]);

  const go = (to: string) => {
    setOpen(false);
    setQ('');
    navigate(to);
  };

  const showPanel = open && needle.length > 0;

  return (
    <div ref={wrap} className="relative min-w-0 max-w-2xl flex-1">
      <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
      <input
        ref={input}
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setOpen(false);
            input.current?.blur();
          } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActive((i) => Math.min(i + 1, flat.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((i) => Math.max(i - 1, 0));
          } else if (e.key === 'Enter' && flat[active]) {
            go(flat[active]!.to);
          }
        }}
        placeholder="Search stores & apps…  ⌘K"
        className="h-10 w-full rounded-lg border bg-muted/40 pl-8 pr-3 text-base outline-none transition-colors placeholder:text-muted-foreground focus:border-ring focus:bg-card sm:h-8.5 sm:text-[13px]"
      />
      {showPanel && (
        <div className="absolute left-0 right-0 top-10 z-50 max-h-[min(24rem,calc(100dvh-5rem))] overflow-y-auto overscroll-contain rounded-xl border bg-card p-1.5 shadow-pop">
          {storeHits.length === 0 && appHits.length === 0 ? (
            <p className="px-3 py-4 text-center text-[13px] text-muted-foreground">
              {overview.isLoading ? 'Loading the app index…' : `Nothing matches “${q.trim()}”.`}
            </p>
          ) : (
            <>
              {storeHits.length > 0 && (
                <>
                  <div className="flex items-center gap-1.5 px-2.5 pb-1 pt-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    <StoreIcon className="size-3" /> Stores
                  </div>
                  {storeHits.map((s, i) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => go(`/stores/${s.id}`)}
                      onMouseEnter={() => setActive(i)}
                      className={cn(
                        'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left',
                        active === i ? 'bg-muted' : 'hover:bg-muted/60',
                      )}
                    >
                      <StoreGlyph color={s.data.color} icon={s.data.icon} seed={s.id} size="sm" />
                      <span className="min-w-0 flex-1 truncate text-[13px] font-medium">{s.data.name}</span>
                      <span className="shrink-0 text-[11px] text-muted-foreground">{s.data.appsCount ?? 0} apps</span>
                    </button>
                  ))}
                </>
              )}
              {appHits.length > 0 && (
                <>
                  <div className="flex items-center gap-1.5 px-2.5 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    <AppWindow className="size-3" /> Apps
                    {appMatches.total > appHits.length && (
                      <span className="font-normal normal-case tracking-normal">· first {appHits.length} of {appMatches.total} — keep typing to narrow</span>
                    )}
                  </div>
                  {appHits.map((a, i) => {
                    const idx = storeHits.length + i;
                    const status = VERSION_STATUS_META[a.status];
                    const StatusIcon = status.icon;
                    return (
                      <button
                        key={`${a.storeId}/${a.appId}`}
                        type="button"
                        onClick={() => go(`/stores/${a.storeId}/apps/${a.appId}`)}
                        onMouseEnter={() => setActive(idx)}
                        className={cn(
                          'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left',
                          active === idx ? 'bg-muted' : 'hover:bg-muted/60',
                        )}
                      >
                        <AppGlyph name={a.appName} iconUrl={a.iconUrl} seed={a.appId} size="sm" className="rounded-[22%]" />
                        <span className="min-w-0 flex-1 truncate text-[13px] font-medium">{a.appName}</span>
                        {a.live && (
                          <Tooltip content="Already available on the App Store — a published version is live">
                            <span className="flex shrink-0 items-center gap-1 rounded-full bg-success/12 px-1.5 py-0.5 text-[10px] font-semibold text-success">
                              <span className="size-1.5 animate-pulse rounded-full bg-success" /> Live
                            </span>
                          </Tooltip>
                        )}
                        <Tooltip content={status.label}>
                          <span className={cn('flex size-6 shrink-0 items-center justify-center rounded-full', status.panel, status.tone)} aria-label={status.label}>
                            <StatusIcon className={cn('size-3.5', a.status === 'inReview' && 'animate-pulse')} />
                          </span>
                        </Tooltip>
                        <PlatformBadges platforms={a.platforms as never} devices={a.devices as never} className="shrink-0" />
                        <span className="flex shrink-0 items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
                          <StoreGlyph color={a.storeColor ?? undefined} icon={a.storeIcon ?? undefined} seed={a.storeId} size="sm" className="size-5 rounded" />
                          {a.storeName}
                        </span>
                      </button>
                    );
                  })}
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
